import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

const ALLOWED_EMAIL_DOMAIN = '@derator.cz';

let msalInstance;
let redirectHandled = false;

const ENV = {
  REACT_APP_ENTRA_TENANT_ID: process.env.REACT_APP_ENTRA_TENANT_ID,
  REACT_APP_ENTRA_CLIENT_ID: process.env.REACT_APP_ENTRA_CLIENT_ID,
  REACT_APP_ENTRA_API_SCOPE: process.env.REACT_APP_ENTRA_API_SCOPE,
  REACT_APP_ENTRA_AUTHORITY: process.env.REACT_APP_ENTRA_AUTHORITY,
  REACT_APP_ENTRA_REDIRECT_URI: process.env.REACT_APP_ENTRA_REDIRECT_URI,
  REACT_APP_ENTRA_POST_LOGOUT_REDIRECT_URI: process.env.REACT_APP_ENTRA_POST_LOGOUT_REDIRECT_URI,
};

function required(name) {
  const value = ENV[name];
  if (!value || !String(value).trim()) {
    throw new Error(`Missing required env var: ${name}`);
  }
  return String(value).trim();
}

function optional(name, fallback = '') {
  const value = ENV[name];
  if (!value || !String(value).trim()) return fallback;
  return String(value).trim();
}

function getAuthority() {
  const explicit = optional('REACT_APP_ENTRA_AUTHORITY');
  if (explicit) return explicit;
  return `https://login.microsoftonline.com/${required('REACT_APP_ENTRA_TENANT_ID')}`;
}

function getRedirectUri() {
  return optional('REACT_APP_ENTRA_REDIRECT_URI', `${window.location.origin}/auth-callback.html`);
}

function getPostLogoutRedirectUri() {
  return optional('REACT_APP_ENTRA_POST_LOGOUT_REDIRECT_URI', window.location.origin);
}

function getCurrentOriginRedirectUri() {
  return `${window.location.origin}/`;
}

function getApiScope() {
  return required('REACT_APP_ENTRA_API_SCOPE');
}

function getMsalConfig() {
  return {
    auth: {
      clientId: required('REACT_APP_ENTRA_CLIENT_ID'),
      authority: getAuthority(),
      redirectUri: getRedirectUri(),
      postLogoutRedirectUri: getPostLogoutRedirectUri(),
      // Keep user on the original SPA route instead of forcing extra navigation hops.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  };
}

export function isRunningInsidePopupWindow() {
  try {
    return Boolean(window.opener && window.opener !== window);
  } catch {
    return false;
  }
}

export function shouldUseRedirectAuth() {
  try {
    const ua = String(window.navigator.userAgent || '').toLowerCase();
    const standalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isEmbeddedVsCode = ua.includes('electron') || ua.includes('vscode');
    return standalone || isIos || isEmbeddedVsCode;
  } catch {
    return false;
  }
}

function ensureMsalInstance() {
  if (msalInstance) return msalInstance;
  msalInstance = new PublicClientApplication(getMsalConfig());
  return msalInstance;
}

export function isEntraConfigured() {
  try {
    required('REACT_APP_ENTRA_TENANT_ID');
    required('REACT_APP_ENTRA_CLIENT_ID');
    required('REACT_APP_ENTRA_API_SCOPE');
    return true;
  } catch {
    return false;
  }
}

export async function initializeEntraAuth() {
  const pca = ensureMsalInstance();
  await pca.initialize();

  if (!redirectHandled) {
    redirectHandled = true;
    const redirectResult = await pca.handleRedirectPromise();
    if (redirectResult && redirectResult.account) {
      pca.setActiveAccount(redirectResult.account);
    }
  }

  const active = pca.getActiveAccount();
  if (active) return active;

  const accounts = pca.getAllAccounts();
  if (accounts.length > 0) {
    pca.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
}

export function getActiveEntraAccount() {
  const pca = ensureMsalInstance();
  const active = pca.getActiveAccount();
  if (active) return active;
  const accounts = pca.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

function createLoginRequest() {
  return {
    scopes: ['openid', 'profile', 'email', getApiScope()],
    prompt: 'select_account',
  };
}

function createApiTokenRequest(account) {
  return {
    account,
    scopes: [getApiScope()],
  };
}

export function isAllowedDomainUser(account) {
  const email = String((account && (account.username || account.name)) || '').trim().toLowerCase();
  return email.endsWith(ALLOWED_EMAIL_DOMAIN);
}

export async function signInWithMicrosoft() {
  if (isRunningInsidePopupWindow()) {
    throw new Error('Toto okno je pouze pro dokončení Microsoft přihlášení. Pokračujte v hlavním okně CRM.');
  }

  const pca = ensureMsalInstance();

  if (shouldUseRedirectAuth()) {
    await pca.loginRedirect({
      ...createLoginRequest(),
      redirectUri: getCurrentOriginRedirectUri(),
    });
    return { account: null, redirected: true };
  }

  const result = await pca.loginPopup(createLoginRequest());
  if (result && result.account) {
    pca.setActiveAccount(result.account);
    return { account: result.account, redirected: false };
  }
  return { account: getActiveEntraAccount(), redirected: false };
}

export async function getApiAccessToken() {
  const pca = ensureMsalInstance();
  const account = getActiveEntraAccount();
  if (!account) return null;

  try {
    const silentResult = await pca.acquireTokenSilent(createApiTokenRequest(account));
    return silentResult && silentResult.accessToken ? silentResult.accessToken : null;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      throw error;
    }

    if (shouldUseRedirectAuth()) {
      await pca.acquireTokenRedirect({
        ...createApiTokenRequest(account),
        redirectUri: getCurrentOriginRedirectUri(),
      });
      return null;
    }

    const popupResult = await pca.acquireTokenPopup(createApiTokenRequest(account));
    return popupResult && popupResult.accessToken ? popupResult.accessToken : null;
  }
}

export async function signOutFromMicrosoft() {
  const pca = ensureMsalInstance();
  const account = getActiveEntraAccount();

   if (shouldUseRedirectAuth()) {
    await pca.logoutRedirect({
      account: account || undefined,
        postLogoutRedirectUri: getCurrentOriginRedirectUri(),
    });
    return;
  }

  await pca.logoutPopup({
    account: account || undefined,
    mainWindowRedirectUri: getPostLogoutRedirectUri(),
  });
}
